import { useEffect, useRef } from "react";

/**
 * Real-time push for Gate 1/Gate 2/Final Review status changes (2026-09,
 * extracted 2026-09-08 so HISTORY can share it with REVIEWS) — direct
 * answer to a live user complaint that this app relies entirely on a
 * polling timer while every other app they use pushes updates instantly.
 * Subscribes to `GET .../review-events` (an SSE stream — see that
 * route's own doc comment in video-routes.ts) and calls `onChange`
 * immediately whenever the server reports a write for this tenant,
 * instead of waiting for the next poll tick. The event bus behind that
 * route is wrapped around every write-capable persona-source-still/
 * video-processing-job/video repository method (`save`, `decideReview`,
 * `createVideoWithGenerationJob`, ...) — including `DecideFinalVideoReview`'s
 * own `videoRepository.save()` call, so a Gate 3 Final Approve/Reject
 * publishes on the exact same channel as every Gate 1/Gate 2 write, with
 * no separate wiring needed per caller.
 *
 * Callers are expected to keep their own slower polling interval as a
 * safety net (never removed) — this hook's own connection can drop
 * (network blip, server restart, `reviewEventBus` not wired in this
 * deployment) and `EventSource` retries on its own schedule, but a
 * caller must never depend on push ALONE ever reaching it. `onChange` is
 * read via a ref so the `EventSource` connection itself is opened
 * exactly once per `tenantId` — never torn down and reopened just
 * because the caller's own callback identity changed on a re-render.
 */
export function useReviewEventStream(tenantId: string, onChange: () => void): void {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const source = new EventSource(`/videos/${tenantId}/review-events`);
    source.onmessage = () => onChangeRef.current();
    return () => source.close();
  }, [tenantId]);
}
